import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";

// The cards on the extra tab, one editable group each. The nine groups a
// candidate sees on the Rentmatch property page (general, outside, inside,
// comfort, energy, house rules, condition, structural, risk) are named the
// same here, so a broker filling this tab recognizes what a candidate reads.
// The rest are dashboard-only: the meters, the EPC certificate, bookkeeping
// and the media links.
export enum EExtraInfoGroup {
  General = "general",
  Epc = "epc",
  Bookkeeping = "bookkeeping",
  BookkeepingGroup = "bookkeeping_group",
  Electricity = "electricity",
  NaturalGas = "natural_gas",
  Water = "water",
  Exterior = "exterior",
  Interior = "interior",
  Comfort = "comfort",
  Energy = "energy",
  HouseRules = "house_rules",
  Condition = "condition",
  Structural = "structural",
  Risk = "risk",
  Media = "media",
}

export interface IExtraData {
  property: IProperty;
  propertyAmenities?: IPropertyAmenity[];
  extraInfoGroup: EExtraInfoGroup;
}

export enum EField {
  Header = "_header",

  // EPC
  // Custom EPC-schema, plus the two levels from the EPB report
  ELevel = "eLevel",
  KLevel = "kLevel",

  // Bookkeeping
  CostUnit = "costUnit",
  BookkeepingPaidOutStartDate = "bookkeepingPaidOutStartDate",

  // Bookkeeping Group
  CostCenter = "costCenter",

  // Electricity
  ElectricityType = "electricityType",
  ElectricityEan = "electricityEan",
  ElectricityMeterNumberBoth = "electricityMeterNumberBoth",
  ElectricityMeterType = "electricityMeterType",

  // General
  Price = "price",
  Costs = "costs",
  CostsComment = "costsComment",
  AvailableAtDate = "availableAtDate",
  RoomCountBedroom = "roomCountBedroom",
  RoomCountBathroom = "roomCountBathroom",
  LivingSurfaceArea = "livingSurfaceArea",
  BuildingFloor = "buildingFloor",
  BuildingFloorCount = "buildingFloorCount",

  // Exterior
  GroundSurfaceArea = "groundSurfaceArea",
  BuildingSurfaceArea = "buildingSurfaceArea",
  HasGarden = "hasGarden",
  BuildingGardenSurfaceArea = "buildingGardenSurfaceArea",
  GardenOrientation = "gardenOrientation",
  HasTerrace = "hasTerrace",
  BuildingTerraceSurfaceArea = "buildingTerraceSurfaceArea",
  TerraceOrientation = "terraceOrientation",
  HasGardenShed = "hasGardenShed",
  HasPool = "hasPool",
  NumberOfParkingsOutside = "numberOfParkingsOutside",

  // Interior
  NumberOfToilets = "numberOfToilets",
  NumberOfShowerRooms = "numberOfShowerRooms",
  HasAttic = "hasAttic",
  BuildingHasStorage = "buildingHasStorage",
  IsFurnished = "isFurnished",
  KitchenType = "kitchenType",
  FloorType = "floorType",
  BuildingHasHardMaterials = "buildingHasHardMaterials",
  HasOven = "hasOven",
  HasFridge = "hasFridge",
  HasDishWasher = "hasDishwasher",
  HasRangeHood = "hasRangeHood",
  RoomCountGarage = "roomCountGarage",
  NumberOfParkingsInside = "numberOfParkingsInside",

  // Comfort
  HasLift = "hasLift",
  HasAirConditioning = "hasAirConditioning",
  HasAlarm = "hasAlarm",
  HasVideophone = "hasVideophone",
  HasSmokeDetectors = "hasSmokeDetectors",
  AccessibleForDisabled = "accessibleForDisabled",

  // Energy
  HasCentralHeating = "hasCentralHeating",
  HeatingType = "heatingType",
  HeatingDistribution = "heatingDistribution",
  HasDoubleGlazing = "hasDoubleGlazing",
  GlazingType = "glazingType",
  WindowFrameMaterial = "windowFrameMaterial",
  HasInsulation = "hasInsulation",
  HasSolarPanels = "hasSolarPanels",
  IsLowEnergy = "isLowEnergy",
  IsPassiveHouse = "isPassiveHouse",
  Co2Emission = "co2Emission",

  // House rules
  PetsAllowed = "petsAllowed",
  PetsAllowedDetail = "petsAllowedDetail",
  SmokingAllowed = "smokingAllowed",

  // Condition and surroundings
  Condition = "condition",
  ConstructionYear = "constructionYear",
  RenovationYear = "renovationYear",
  EnvironmentType = "environmentType",

  // Structural
  BuildingAdjoined = "buildingAdjoined",
  FrontWidth = "frontWidth",
  Volume = "volume",
  RoofType = "roofType",
  RoofMaterial = "roofMaterial",

  // Risk and certificates
  FloodZoneGScore = "floodZoneGScore",
  FloodZonePScore = "floodZonePScore",
  ConformityCertificate = "conformityCertificate",
  ElectricityCertificate = "electricityCertificate",
  AsbestosCertificate = "asbestosCertificate",
  AsbestosCertificateDate = "asbestosCertificateDate",

  // Media
  VirtualTourUrl = "virtualTourUrl",
  VideoUrl = "videoUrl",
  Model3dUrl = "model3dUrl",
  FloorPlanUrl = "floorPlanUrl",

  // Natural Gas
  NaturalGasAvailable = "naturalGasAvailable",
  NaturalGasEan = "naturalGasEan",
  NaturalGasMeterNumber = "naturalGasMeterNumber",
  NaturalGasMeterType = "naturalGasMeterType",

  // Water
  WaterMeterNumber = "waterMeterNumber",
}
